import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { 
  Download, Upload, Database, FileSpreadsheet, CheckCircle, AlertTriangle, 
  RefreshCw, X, ArrowRight, Eye, Trash2, FileText, Users, AlertCircle,
  ChevronDown, ChevronRight, Info, Check, XCircle, UserPlus, Stethoscope,
  Copy, ClipboardCheck
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const DataImportPage = () => {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Import type selection
  const [importType, setImportType] = useState(null); // 'patients', 'staff', 'physicians'
  
  // Import wizard state
  const [wizardStep, setWizardStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [validationResults, setValidationResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  
  // Add sendInviteEmails state
  const [sendInviteEmails, setSendInviteEmails] = useState(false);
  
  // View details modal
  const [viewingImport, setViewingImport] = useState(null);

  // Import type configurations
  const importTypeConfig = {
    patients: {
      title: 'Patient Import',
      icon: Users,
      color: 'teal',
      bgColor: 'bg-teal-500',
      hoverColor: 'hover:bg-teal-600',
      endpoints: {
        template: '/import/patient-template',
        preview: '/import/preview',
        validate: '/import/validate',
        import: '/import/patients'
      },
      fieldsKey: 'patientFields',
      templateFile: 'patient_import_template.csv'
    },
    staff: {
      title: 'Staff Import',
      icon: UserPlus,
      color: 'blue',
      bgColor: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      endpoints: {
        template: '/import/staff-template',
        preview: '/import/staff/preview',
        validate: '/import/staff/validate',
        import: '/import/staff'
      },
      fieldsKey: 'staffFields',
      templateFile: 'staff_import_template.csv'
    },
    physicians: {
      title: 'Physician Import',
      icon: Stethoscope,
      color: 'purple',
      bgColor: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      endpoints: {
        template: '/import/physician-template',
        preview: '/import/physician/preview',
        validate: '/import/physician/validate',
        import: '/import/physicians'
      },
      fieldsKey: 'physicianFields',
      templateFile: 'physician_import_template.csv'
    }
  };

  useEffect(() => {
    fetchImportHistory();
  }, []);

  const fetchImportHistory = async () => {
    try {
      const res = await api.get('/import/history');
      setImports(res.data.history || []);
    } catch (error) {
      console.error('Error fetching import history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectImportType = (type) => {
    setImportType(type);
    setWizardStep(1);
  };

  const handleFileSelect = useCallback(async (file) => {
    if (!file || !importType) return;
    
    setSelectedFile(file);
    setPreviewData(null);
    setColumnMapping({});
    setValidationResults(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const config = importTypeConfig[importType];
      const res = await api.post(config.endpoints.preview, formData);
      setPreviewData(res.data);
      setColumnMapping(res.data.suggestedMapping || {});
      setWizardStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to read CSV file');
      setWizardStep(1);
    }
  }, [importType]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.csv') || file.name.endsWith('.xlsx'))) {
      handleFileSelect(file);
    } else {
      toast.error('Please upload a CSV or Excel file');
    }
  };

  const handleValidate = async () => {
    if (!selectedFile || !importType) return;
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('mapping', JSON.stringify(columnMapping));
    
    try {
      const config = importTypeConfig[importType];
      const res = await api.post(config.endpoints.validate, formData);
      setValidationResults(res.data);
      setWizardStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Validation failed');
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !importType) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('mapping', JSON.stringify(columnMapping));
    formData.append('skipDuplicates', skipDuplicates);
    
    // Add sendInviteEmails for staff import
    if (importType === 'staff') {
      formData.append('sendInviteEmails', sendInviteEmails);
    }
    
    try {
      const config = importTypeConfig[importType];
      const res = await api.post(config.endpoints.import, formData);
      setImportResult(res.data);
      setWizardStep(4);
      fetchImportHistory();
      
      // Show appropriate success message
      if (importType === 'staff' && sendInviteEmails && res.data.emailResults?.sent > 0) {
        toast.success(`Successfully imported ${res.data.summary.successful} staff and sent ${res.data.emailResults.sent} welcome emails!`);
      } else {
        toast.success(`Successfully imported ${res.data.summary.successful} records!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async (type) => {
    try {
      const config = importTypeConfig[type];
      const res = await api.get(config.endpoints.template);
      
      const blob = new Blob([res.data.sampleCSV], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = config.templateFile;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const resetWizard = () => {
    setWizardStep(0);
    setImportType(null);
    setSelectedFile(null);
    setPreviewData(null);
    setColumnMapping({});
    setValidationResults(null);
    setImportResult(null);
    setSendInviteEmails(false);
  };

  const handleDeleteImport = async (importId) => {
    if (!window.confirm('Delete this import record?')) return;
    try {
      await api.delete(`/import/history/${importId}`);
      toast.success('Import record deleted');
      fetchImportHistory();
    } catch (error) {
      toast.error('Failed to delete import record');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Completed':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Completed</span>;
      case 'Processing':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Processing</span>;
      case 'Failed':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Failed</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'Patients':
        return <span className="px-2 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">Patients</span>;
      case 'Staff':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">Staff</span>;
      case 'Physicians':
        return <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">Physicians</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{type}</span>;
    }
  };

  const currentConfig = importType ? importTypeConfig[importType] : null;
  const fields = previewData ? previewData[currentConfig?.fieldsKey] || previewData.patientFields || {} : {};

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="data-import-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#1abc9c] to-[#16a085] rounded-lg flex items-center justify-center shadow-lg">
              <Database className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Data Import Center</h1>
              <p className="text-gray-600">Import Patients, Staff, and Physicians from CSV</p>
            </div>
          </div>
        </div>

        {/* Import Type Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Patient Import Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-4 text-white">
              <Users className="w-8 h-8 mb-2" />
              <h3 className="text-lg font-semibold">Patient Import</h3>
              <p className="text-sm text-teal-100">Import patient records from Alora Plus</p>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-600">Fields: Name, DOB, Phone, Address, Insurance, Diagnosis</p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('patients')}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-1" /> Template
                </Button>
                <Button 
                  size="sm" 
                  className="flex-1 bg-teal-500 hover:bg-teal-600"
                  onClick={() => handleSelectImportType('patients')}
                  data-testid="import-patients-btn"
                >
                  <Upload className="w-4 h-4 mr-1" /> Import
                </Button>
              </div>
            </div>
          </div>

          {/* Staff Import Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 text-white">
              <UserPlus className="w-8 h-8 mb-2" />
              <h3 className="text-lg font-semibold">Staff Import</h3>
              <p className="text-sm text-blue-100">Import team members with login access</p>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-600">Fields: Name, Email, Role, Department, License</p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('staff')}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-1" /> Template
                </Button>
                <Button 
                  size="sm" 
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  onClick={() => handleSelectImportType('staff')}
                  data-testid="import-staff-btn"
                >
                  <Upload className="w-4 h-4 mr-1" /> Import
                </Button>
              </div>
            </div>
          </div>

          {/* Physician Import Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 text-white">
              <Stethoscope className="w-8 h-8 mb-2" />
              <h3 className="text-lg font-semibold">Physician Import</h3>
              <p className="text-sm text-purple-100">Import referring physicians</p>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-600">Fields: Name, NPI, Specialty, Practice, Contact</p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadTemplate('physicians')}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-1" /> Template
                </Button>
                <Button 
                  size="sm" 
                  className="flex-1 bg-purple-500 hover:bg-purple-600"
                  onClick={() => handleSelectImportType('physicians')}
                  data-testid="import-physicians-btn"
                >
                  <Upload className="w-4 h-4 mr-1" /> Import
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Guide */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">How to Import from Alora Plus</h3>
              <ol className="mt-2 text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li><strong>Patients:</strong> Alora Plus → Reports → Patient List → Export CSV</li>
                <li><strong>Staff:</strong> Alora Plus → Customize → User/Security Manager → Export</li>
                <li><strong>Physicians:</strong> Alora Plus → Setup → Referring Physicians → Export</li>
                <li>Click the <strong>"Import"</strong> button for the data type you want to import</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Import History */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Import History</h3>
            <button onClick={fetchImportHistory} className="text-gray-500 hover:text-gray-700">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
            </div>
          ) : imports.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No imports yet. Select an import type above to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">File Name</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Imported</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Failed</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Skipped</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {imports.map((imp) => (
                    <tr key={imp.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{getTypeBadge(imp.type)}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{imp.fileName}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{imp.totalRecords}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-semibold">{imp.successfulRecords}</td>
                      <td className="px-4 py-3 text-center text-red-600 font-semibold">{imp.failedRecords}</td>
                      <td className="px-4 py-3 text-center text-yellow-600 font-semibold">{imp.skippedDuplicates || 0}</td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(imp.status)}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {new Date(imp.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setViewingImport(imp)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteImport(imp.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Import Wizard Modal */}
      {wizardStep > 0 && currentConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className={`p-4 border-b ${currentConfig.bgColor} text-white flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                {React.createElement(currentConfig.icon, { className: "w-6 h-6" })}
                <h2 className="text-xl font-semibold">{currentConfig.title}</h2>
              </div>
              <button onClick={resetWizard} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Progress Steps */}
            <div className="px-6 py-4 border-b bg-gray-50">
              <div className="flex items-center justify-between max-w-2xl mx-auto">
                {['Upload', 'Map Columns', 'Validate', 'Complete'].map((step, i) => (
                  <div key={step} className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      wizardStep > i + 1 ? 'bg-green-500 text-white' :
                      wizardStep === i + 1 ? `${currentConfig.bgColor} text-white` :
                      'bg-gray-200 text-gray-500'
                    }`}>
                      {wizardStep > i + 1 ? <Check className="w-4 h-4" /> : i + 1}
                    </div>
                    <span className={`ml-2 text-sm ${wizardStep >= i + 1 ? 'text-gray-900' : 'text-gray-400'}`}>
                      {step}
                    </span>
                    {i < 3 && <ChevronRight className="w-5 h-5 text-gray-300 mx-4" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Step 1: Upload */}
              {wizardStep === 1 && (
                <div className="max-w-lg mx-auto">
                  <div 
                    className={`border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-${currentConfig.color}-500 transition-colors cursor-pointer`}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('csv-upload').click()}
                    data-testid="file-drop-zone"
                  >
                    <input
                      type="file"
                      id="csv-upload"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => handleFileSelect(e.target.files[0])}
                      className="hidden"
                    />
                    <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-700 mb-2">
                      Drag & drop your CSV file here
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      or click to browse
                    </p>
                    <p className="text-xs text-gray-400">
                      Supports .csv, .xlsx, .xls files up to 10MB
                    </p>
                  </div>
                </div>
              )}

              {/* Step 2: Column Mapping */}
              {wizardStep === 2 && previewData && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">Map CSV Columns to Fields</h3>
                      <p className="text-sm text-gray-500">
                        {previewData.fileName} - {previewData.totalRows} rows detected
                      </p>
                    </div>
                    {previewData.missingRequired?.length > 0 && (
                      <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {previewData.missingRequired.length} required fields unmapped
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(fields).map(([field, info]) => (
                      <div key={field} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                            {info.label}
                            {info.required && <span className="text-red-500">*</span>}
                          </label>
                          <select
                            value={columnMapping[field] || ''}
                            onChange={(e) => setColumnMapping({...columnMapping, [field]: e.target.value})}
                            className={`w-full mt-1 px-3 py-2 border rounded-lg text-sm ${
                              info.required && !columnMapping[field] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            data-testid={`mapping-${field}`}
                          >
                            <option value="">-- Select Column --</option>
                            {previewData.headers.map(header => (
                              <option key={header} value={header}>{header}</option>
                            ))}
                          </select>
                        </div>
                        {columnMapping[field] && (
                          <Check className="w-5 h-5 text-green-500" />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Preview Table */}
                  {previewData.previewRows?.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-medium text-gray-900 mb-3">Preview (first {previewData.previewRows.length} rows)</h4>
                      <div className="border rounded-lg overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left">#</th>
                              {Object.entries(fields).slice(0, 5).map(([field, info]) => (
                                <th key={field} className="px-3 py-2 text-left">{info.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {previewData.previewRows.slice(0, 5).map((row, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                                {Object.keys(fields).slice(0, 5).map(field => (
                                  <td key={field} className="px-3 py-2">
                                    {row[columnMapping[field]] || '-'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Validation */}
              {wizardStep === 3 && validationResults && (
                <div className="space-y-6">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-gray-800">{validationResults.totalRows}</div>
                      <div className="text-sm text-gray-500">Total Rows</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">{validationResults.validRows}</div>
                      <div className="text-sm text-green-700">Valid</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-red-600">{validationResults.errorRows}</div>
                      <div className="text-sm text-red-700">Errors</div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-yellow-600">{validationResults.duplicatesInFile?.length || 0}</div>
                      <div className="text-sm text-yellow-700">Duplicates</div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={skipDuplicates}
                        onChange={(e) => setSkipDuplicates(e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300"
                      />
                      <div>
                        <span className="font-medium text-gray-900">Skip duplicates</span>
                        <p className="text-sm text-gray-500">
                          {importType === 'staff' ? 'Staff with matching email' : 
                           importType === 'physicians' ? 'Physicians with matching NPI' :
                           'Patients with matching name and DOB'} will be skipped
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Email option for staff import */}
                  {importType === 'staff' && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={sendInviteEmails}
                          onChange={(e) => setSendInviteEmails(e.target.checked)}
                          className="w-5 h-5 rounded border-blue-300"
                          data-testid="send-invite-emails-checkbox"
                        />
                        <div>
                          <span className="font-medium text-blue-900 flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Send welcome emails with login credentials
                          </span>
                          <p className="text-sm text-blue-700">
                            Each staff member will receive an email with their temporary password. 
                            If unchecked, you'll see the passwords on screen to share manually.
                          </p>
                        </div>
                      </label>
                    </div>
                  )}

                  {validationResults.errorRows > 0 && (
                    <div className="border border-red-200 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 bg-red-50 border-b border-red-200">
                        <h4 className="font-medium text-red-800">Validation Errors</h4>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {validationResults.validationResults
                          .filter(r => !r.valid)
                          .slice(0, 20)
                          .map((result, i) => (
                            <div key={i} className="px-4 py-2 border-b border-red-100 last:border-0 text-sm">
                              <span className="font-medium text-red-700">Row {result.row}:</span>
                              <span className="text-red-600 ml-2">{result.errors.join(', ')}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {validationResults.canImport && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Ready to import!</p>
                        <p className="text-sm text-green-700">
                          {validationResults.validRows} records will be imported.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Complete */}
              {wizardStep === 4 && importResult && (
                <div className="max-w-lg mx-auto text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Import Complete!</h3>
                  <p className="text-gray-600 mb-6">Your data has been successfully imported.</p>
                  
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-3xl font-bold text-green-600">{importResult.summary.successful}</div>
                      <div className="text-sm text-green-700">Imported</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="text-3xl font-bold text-red-600">{importResult.summary.failed}</div>
                      <div className="text-sm text-red-700">Failed</div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <div className="text-3xl font-bold text-yellow-600">{importResult.summary.skippedDuplicates}</div>
                      <div className="text-sm text-yellow-700">Skipped</div>
                    </div>
                  </div>

                  {/* Staff Temporary Passwords */}
                  {importType === 'staff' && importResult.temporaryPasswords?.length > 0 && (
                    <div className="text-left mb-6 border border-blue-200 rounded-lg overflow-hidden">
                      <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 font-medium text-blue-800 flex items-center justify-between">
                        <span>Temporary Login Credentials</span>
                        <button 
                          onClick={() => {
                            const text = importResult.temporaryPasswords.map(
                              p => `${p.name}\nEmail: ${p.email}\nPassword: ${p.tempPassword}\n`
                            ).join('\n---\n');
                            copyToClipboard(text);
                          }}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                        >
                          <Copy className="w-4 h-4" /> Copy All
                        </button>
                      </div>
                      <div className="max-h-48 overflow-y-auto text-sm">
                        {importResult.temporaryPasswords.map((cred, i) => (
                          <div key={i} className="px-4 py-3 border-b border-blue-100 last:border-0 flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{cred.name}</p>
                              <p className="text-gray-500">{cred.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <code className="bg-gray-100 px-2 py-1 rounded text-xs">{cred.tempPassword}</code>
                              <button 
                                onClick={() => copyToClipboard(cred.tempPassword)}
                                className="p-1 hover:bg-gray-100 rounded"
                              >
                                <Copy className="w-4 h-4 text-gray-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="px-4 py-2 bg-amber-50 text-amber-700 text-xs">
                        ⚠️ Share these credentials securely. Users will be prompted to change their password on first login.
                      </div>
                    </div>
                  )}

                  {/* Email Results for Staff Import */}
                  {importType === 'staff' && importResult.emailResults && (
                    <div className="text-left mb-6 border border-green-200 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 bg-green-50 border-b border-green-200 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">
                          Welcome emails sent successfully: {importResult.emailResults.sent || 0}
                        </span>
                      </div>
                      {importResult.emailResults.failed > 0 && (
                        <div className="px-4 py-2 bg-red-50 text-red-700 text-sm">
                          Failed to send: {importResult.emailResults.failed} emails
                        </div>
                      )}
                      <div className="px-4 py-2 bg-blue-50 text-blue-700 text-xs">
                        📧 Staff members will receive an email with their login credentials and will be prompted to change their password on first login.
                      </div>
                    </div>
                  )}

                  {importResult.errors?.length > 0 && (
                    <div className="text-left mb-6 border border-red-200 rounded-lg overflow-hidden">
                      <div className="px-4 py-2 bg-red-50 border-b border-red-200 font-medium text-red-800">
                        Import Errors
                      </div>
                      <div className="max-h-32 overflow-y-auto text-sm">
                        {importResult.errors.map((err, i) => (
                          <div key={i} className="px-4 py-2 border-b border-red-100 last:border-0">
                            <span className="text-red-700">Row {err.row}:</span>
                            <span className="text-red-600 ml-2">{err.errors.join(', ')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-center gap-3">
                    <Button variant="outline" onClick={resetWizard}>
                      Import More
                    </Button>
                    <Button 
                      className={`${currentConfig.bgColor} ${currentConfig.hoverColor}`}
                      onClick={() => { 
                        resetWizard(); 
                        window.location.href = importType === 'patients' ? '/dashboard/patients' : 
                                               importType === 'staff' ? '/dashboard/setup/users' : 
                                               '/dashboard/setup/physicians'; 
                      }}
                    >
                      View {importType === 'patients' ? 'Patients' : importType === 'staff' ? 'Staff' : 'Physicians'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            {wizardStep < 4 && (
              <div className="p-4 border-t bg-gray-50 flex justify-between">
                <Button 
                  variant="outline" 
                  onClick={() => wizardStep > 1 ? setWizardStep(wizardStep - 1) : resetWizard()}
                >
                  {wizardStep === 1 ? 'Cancel' : 'Back'}
                </Button>
                
                {wizardStep === 2 && (
                  <Button 
                    className={`${currentConfig.bgColor} ${currentConfig.hoverColor}`}
                    onClick={handleValidate}
                    disabled={Object.keys(fields).filter(f => fields[f].required).some(f => !columnMapping[f])}
                    data-testid="validate-btn"
                  >
                    Validate Data <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
                
                {wizardStep === 3 && (
                  <Button 
                    className={`${currentConfig.bgColor} ${currentConfig.hoverColor}`}
                    onClick={handleImport}
                    disabled={importing || !validationResults?.canImport}
                    data-testid="import-btn"
                  >
                    {importing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        Import {validationResults?.validRows} Records
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* View Import Details Modal */}
      {viewingImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Import Details</h3>
              <button onClick={() => setViewingImport(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Type</span>
                  <p>{getTypeBadge(viewingImport.type)}</p>
                </div>
                <div>
                  <span className="text-gray-500">File Name</span>
                  <p className="font-medium">{viewingImport.fileName}</p>
                </div>
                <div>
                  <span className="text-gray-500">Status</span>
                  <p>{getStatusBadge(viewingImport.status)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Total Records</span>
                  <p className="font-medium">{viewingImport.totalRecords}</p>
                </div>
                <div>
                  <span className="text-gray-500">Successfully Imported</span>
                  <p className="font-medium text-green-600">{viewingImport.successfulRecords}</p>
                </div>
                <div>
                  <span className="text-gray-500">Failed</span>
                  <p className="font-medium text-red-600">{viewingImport.failedRecords}</p>
                </div>
                <div>
                  <span className="text-gray-500">Skipped (Duplicates)</span>
                  <p className="font-medium text-yellow-600">{viewingImport.skippedDuplicates || 0}</p>
                </div>
                <div>
                  <span className="text-gray-500">Import Date</span>
                  <p className="font-medium">{new Date(viewingImport.createdAt).toLocaleString()}</p>
                </div>
              </div>

              {viewingImport.errors?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-red-700 mb-2">Errors ({viewingImport.errors.length})</h4>
                  <div className="max-h-40 overflow-y-auto text-sm space-y-1">
                    {viewingImport.errors.map((err, i) => (
                      <div key={i} className="text-red-600">
                        Row {err.row}: {err.errors.join(', ')}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default DataImportPage;
